import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/home/euql1n/Workspaces/personal/chonky-website/2.x_docz/node_modules/gatsby-theme-docz/src/base/Layout.js";
import { FullFileBrowser } from 'chonky';
import { Playground } from 'docz';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1 {...{
      "id": "installation--usage"
    }}>{`Installation & usage`}</h1>
    <h2 {...{
      "id": "installation"
    }}>{`Installation`}</h2>
    <p>{`You need to install `}<inlineCode parentName="p">{`chonky`}</inlineCode>{` and its default icon component, `}<inlineCode parentName="p">{`chonky-icon-fontawesome`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install chonky@latest chonky-icon-fontawesome@latest
`}</code></pre>
    <p>{`The icon component is packaged separately to decrease the bundle size. You can always
replace it with a custom icon component later.`}</p>
    <p>{`Note that `}<inlineCode parentName="p">{`@latest`}</inlineCode>{` tag is not strictly required, but it's useful to include. It makes
sure you get the most recent bug fixes when you re-install Chonky in an existing
project.`}</p>
    <h2 {...{
      "id": "basic-usage"
    }}>{`Basic usage`}</h2>
    <p>{`As the first step, you should tell Chonky which icon component it should use. This is
done using the `}<inlineCode parentName="p">{`setChonkyDefaults`}</inlineCode>{` helper method:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`import { setChonkyDefaults } from 'chonky';
import { ChonkyIconFA } from 'chonky-icon-fontawesome';

// Somewhere in your \`index.ts\`:
setChonkyDefaults({ iconComponent: ChonkyIconFA });
`}</code></pre>
    <p>{`Then you can use Chonky anywhere in your application:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import { FullFileBrowser } from 'chonky';

export const MyFileBrowser = () => {
    const files = [
        { id: 'lht', name: 'Projects', isDir: true },
        {
            id: 'mcd',
            name: 'chonky-sphere-v2.png',
            thumbnailUrl: 'https://chonky.io/chonky-sphere-v2.png',
        },
    ];
    const folderChain = [{ id: 'xcv', name: 'Demo', isDir: true }];

    return (
        <div style={{ height: 300 }}>
            <FullFileBrowser files={files} folderChain={folderChain} />
        </div>
    );
};
`}</code></pre>
    <p>{`Example below shows a minimal setup of Chonky. Note that the file browser will fill the
height of its parent container, which in this case is `}<inlineCode parentName="p">{`300px`}</inlineCode>{`.`}</p>
    <Playground __position={0} __code={'() => {\n  const files = [\n    { id: \'lht\', name: \'Projects\', isDir: true },\n    {\n      id: \'mcd\',\n      name: \'chonky-sphere-v2.png\',\n      thumbnailUrl: \'https://chonky.io/chonky-sphere-v2.png\',\n    },\n  ]\n  const folderChain = [{ id: \'xcv\', name: \'Demo\', isDir: true }]\n  return (\n    <div style={{ height: 300 }}>\n      <FullFileBrowser files={files} folderChain={folderChain} />\n    </div>\n  )\n}'} __scope={{
      props,
      DefaultLayout,
      FullFileBrowser,
      Playground,
      DefaultLayout,
      _frontmatter
    }} mdxType="Playground">
    {() => {
        const files = [{
          id: 'lht',
          name: 'Projects',
          isDir: true
        }, {
          id: 'mcd',
          name: 'chonky-sphere-v2.png',
          thumbnailUrl: 'https://chonky.io/chonky-sphere-v2.png'
        }];
        const folderChain = [{
          id: 'xcv',
          name: 'Demo',
          isDir: true
        }];
        return <div style={{
          height: 300
        }}>
                <FullFileBrowser files={files} folderChain={folderChain} mdxType="FullFileBrowser" />
            </div>;
      }}
    </Playground>
    <h2 {...{
      "id": "usage-with-typescript"
    }}>{`Usage with Typescript`}</h2>
    <p>{`Chonky was built using Typescript. For your static typing needs, many of the internal
types and generic interfaces are exported.`}</p>
    <p>{`If you're also using Typescript, you can add Chonky types to your project using
standard ES6 imports:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`// FileArray is a TS type
import { FileArray } from 'chonky';

const myFiles: FileArray = [{ id: 'abD3', name: 'README.txt' }, null];
`}</code></pre>
    <p>{`You can see the list of exported types in Chonky's `}<a parentName="p" {...{
        "href": "https://github.com/TimboKZ/Chonky/blob/2.x/packages/chonky/src/index.ts"
      }}>{`entrypoint `}<inlineCode parentName="a">{`index.ts`}</inlineCode>{` on
GitHub`}</a>{`
(files named `}<inlineCode parentName="p">{`*.types`}</inlineCode>{` contain the types). If you need access to types that are not
exported, either `}<a parentName="p" {...{
        "href": "https://github.com/TimboKZ/Chonky/issues"
      }}>{`create an issue`}</a>{` or import
them using the full file path:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`import { MouseFileClickPayload } from 'chonky/lib/types/action-payloads.types';

const myPayload: MouseFileClickPayload = {
    /* ... */
};
`}</code></pre>
    <p>{`While this is not required, I strongly recommend you use the `}<a parentName="p" {...{
        "href": "https://github.com/joonhocho/tsdef"
      }}><inlineCode parentName="a">{`tsdef`}</inlineCode>{` library`}</a>{` in
your Typescript code. It provides many useful generic types and interfaces to make your
TS code cleaner:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`// Install
npm install -D tsdef

// Use
import {AnyFunction, Nullable} from 'tsdef';

const myFunctionOrNull = Nullable<AnyFunction>;
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      